// src/components/diary/DiaryManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Users, BookOpen, Plus, Search, Filter, 
  Download, Upload, Trash2, Edit3, X, Loader2, AlertCircle,
  CheckCircle, FileText, LayoutTemplate, Sparkles, Settings,
  ChevronRight, MoreVertical
} from 'lucide-react';
import { diaryService } from '../../services/diaryService';
import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';

const MEETING_STATUS = {
  PLANNED: { label: 'Planned', color: 'blue', bg: 'bg-blue-100 text-blue-700' },
  ACTIVE: { label: 'In Progress', color: 'yellow', bg: 'bg-yellow-100 text-yellow-700' },
  COMPLETED: { label: 'Completed', color: 'green', bg: 'bg-green-100 text-green-700' },
  CANCELLED: { label: 'Cancelled', color: 'red', bg: 'bg-red-100 text-red-700' }
};

export const DiaryManager = ({ bookId, book, onClose }) => {
  const [activeView, setActiveView] = useState('meetings');
  const [meetings, setMeetings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiContext, setAIContext] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    loadData();
  }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [meetingsData, contactsData, entriesData, templatesData, statsData] = await Promise.all([
        diaryService.getMeetings(bookId),
        diaryService.getContacts(bookId),
        diaryService.getJournalEntries(bookId),
        diaryService.getMeetingTemplates(bookId),
        diaryService.getStats(bookId)
      ]);

      setMeetings(meetingsData);
      setContacts(contactsData);
      setJournalEntries(entriesData);
      setTemplates(templatesData);
      setStats(statsData);
      setAIContext(book?.diarySettings?.aiContext || '');
    } catch (err) {
      console.error('Error loading diary data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeeting = async (meetingData) => {
    try {
      const newMeeting = await diaryService.createMeeting(bookId, {
        ...meetingData,
        status: 'PLANNED',
        participants: [],
        agenda: [],
        minutes: [],
        objectives: [],
        meetingNotes: []
      });
      
      setMeetings(prev => [newMeeting, ...prev]);
      setSuccess('Meeting created successfully');
      setShowAddModal(false);
    } catch (err) {
      setError('Failed to create meeting');
    }
  };

  const handleUpdateMeeting = async (meetingId, updates) => {
    try {
      await diaryService.updateMeeting(bookId, meetingId, updates);
      setMeetings(prev => prev.map(m => m.id === meetingId ? { ...m, ...updates } : m));
      setSuccess('Meeting updated');
      setEditingItem(null);
    } catch (err) {
      setError('Failed to update meeting');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Delete this meeting?')) return;
    
    try {
      await diaryService.deleteMeeting(bookId, meetingId);
      setMeetings(prev => prev.filter(m => m.id !== meetingId));
      setSuccess('Meeting deleted');
    } catch (err) {
      setError('Failed to delete meeting');
    }
  };

  const handleCreateContact = async (contactData) => {
    try {
      const newContact = await diaryService.createContact(bookId, contactData);
      setContacts(prev => [newContact, ...prev]);
      setSuccess('Contact created');
      setShowAddModal(false);
    } catch (err) {
      setError('Failed to create contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    
    try {
      await diaryService.deleteContact(bookId, contactId);
      setContacts(prev => prev.filter(c => c.id !== contactId));
      setSuccess('Contact deleted');
    } catch (err) {
      setError('Failed to delete contact');
    }
  };

  const handleCreateJournalEntry = async (entryData) => {
    try {
      const newEntry = await diaryService.createJournalEntry(bookId, {
        ...entryData,
        date: new Date().toISOString().split('T')[0],
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });
      setJournalEntries(prev => [newEntry, ...prev]);
      setSuccess('Entry created');
      setShowAddModal(false);
    } catch (err) {
      setError('Failed to create journal entry');
    }
  };

  const handleDeleteJournalEntry = async (entryId) => {
    if (!window.confirm('Delete this journal entry?')) return;
    
    try {
      await diaryService.deleteJournalEntry(bookId, entryId);
      setJournalEntries(prev => prev.filter(e => e.id !== entryId));
      setSuccess('Entry deleted');
    } catch (err) {
      setError('Failed to delete entry');
    }
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      const newTemplate = await diaryService.createMeetingTemplate(bookId, templateData);
      setTemplates(prev => [...prev, newTemplate]);
      setSuccess('Template saved');
    } catch (err) {
      setError('Failed to save template');
    }
  };

  const handleSaveAIContext = async () => {
    try {
      await diaryService.saveAIContext(bookId, aiContext);
      setSuccess('AI context saved');
      setShowAIPrompt(false);
    } catch (err) {
      setError('Failed to save AI context');
    }
  };

  const handleGenerateAgenda = async (meetingTitle, objectives) => {
    if (!book?.diarySettings?.geminiApiKey) {
      setError('Please configure your Gemini API key in settings');
      return;
    }

    setIsGenerating(true);
    try {
      const generateAgenda = httpsCallable(functions, 'generateDiaryAgenda');
      const response = await generateAgenda({
        bookId,
        title: meetingTitle,
        objectives,
        context: aiContext
      });
      
      return response.data.agenda;
    } catch (err) {
      setError('Failed to generate agenda');
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const getFilteredItems = () => {
    const query = searchQuery.toLowerCase();
    
    switch (activeView) {
      case 'meetings':
        return meetings.filter(m => 
          m.title?.toLowerCase().includes(query) ||
          m.location?.toLowerCase().includes(query)
        );
      case 'contacts':
        return contacts.filter(c => 
          c.name?.toLowerCase().includes(query) ||
          c.email?.toLowerCase().includes(query)
        );
      case 'journal':
        return journalEntries.filter(e => 
          e.title?.toLowerCase().includes(query) ||
          e.content?.toLowerCase().includes(query)
        );
      case 'templates':
        return templates.filter(t => 
          t.name?.toLowerCase().includes(query)
        );
      default:
        return [];
    }
  };

  const filteredItems = getFilteredItems();

  const renderStats = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
        <Calendar className="h-8 w-8 mb-2 opacity-80" />
        <div className="text-2xl font-bold">{stats?.totalMeetings || 0}</div>
        <div className="text-sm opacity-80">Total Meetings</div>
      </div>
      <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
        <Clock className="h-8 w-8 mb-2 opacity-80" />
        <div className="text-2xl font-bold">{stats?.upcomingMeetings || 0}</div>
        <div className="text-sm opacity-80">Upcoming</div>
      </div>
      <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl p-4 text-white">
        <Users className="h-8 w-8 mb-2 opacity-80" />
        <div className="text-2xl font-bold">{stats?.totalContacts || 0}</div>
        <div className="text-sm opacity-80">Contacts</div>
      </div>
      <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl p-4 text-white">
        <BookOpen className="h-8 w-8 mb-2 opacity-80" />
        <div className="text-2xl font-bold">{stats?.totalJournalEntries || 0}</div>
        <div className="text-sm opacity-80">Journal Entries</div>
      </div>
    </div>
  );

  const renderViewSelector = () => (
    <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-xl">
      {[
        { id: 'meetings', label: 'Meetings', icon: Calendar },
        { id: 'contacts', label: 'Contacts', icon: Users },
        { id: 'journal', label: 'Journal', icon: BookOpen },
        { id: 'templates', label: 'Templates', icon: LayoutTemplate }
      ].map(view => {
        const Icon = view.icon;
        return (
          <button
            key={view.id}
            onClick={() => setActiveView(view.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-medium transition-all ${
              activeView === view.id
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{view.label}</span>
          </button>
        );
      })}
    </div>
  );

  const renderMeetingsList = () => (
    <div className="space-y-3">
      {filteredItems.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No meetings found</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Create Meeting
          </button>
        </div>
      ) : (
        filteredItems.map(meeting => (
          <div
            key={meeting.id}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-gray-900">{meeting.title}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    MEETING_STATUS[meeting.status]?.bg || 'bg-gray-100 text-gray-700'
                  }`}>
                    {MEETING_STATUS[meeting.status]?.label || meeting.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {meeting.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {meeting.time} ({meeting.durationMinutes}m)
                  </span>
                  {meeting.location && (
                    <span className="truncate max-w-[150px]">{meeting.location}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingItem(meeting)}
                  className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteMeeting(meeting.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderContactsList = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {filteredItems.length === 0 ? (
        <div className="col-span-2 text-center py-12 bg-gray-50 rounded-xl">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No contacts found</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Add Contact
          </button>
        </div>
      ) : (
        filteredItems.map(contact => (
          <div
            key={contact.id}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                contact.avatarColor || 'bg-indigo-500'
              }`}>
                {contact.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{contact.name}</h3>
                <p className="text-sm text-gray-500">{contact.category}</p>
                {contact.email && (
                  <p className="text-sm text-gray-600 truncate">{contact.email}</p>
                )}
              </div>
              <button
                onClick={() => handleDeleteContact(contact.id)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderJournalList = () => (
    <div className="space-y-4">
      {filteredItems.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No journal entries found</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Write Entry
          </button>
        </div>
      ) : (
        filteredItems.map(entry => (
          <div
            key={entry.id}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-gray-900">{entry.title}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>{entry.date}</span>
                  <span>•</span>
                  <span>{entry.timestamp}</span>
                  <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">
                    {entry.domain}
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleDeleteJournalEntry(entry.id)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <p className="text-gray-600 text-sm line-clamp-2">{entry.content}</p>
            {entry.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {entry.tags.map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded text-xs">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderTemplatesList = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {filteredItems.length === 0 ? (
        <div className="col-span-2 text-center py-12 bg-gray-50 rounded-xl">
          <LayoutTemplate className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No templates found</p>
        </div>
      ) : (
        filteredItems.map(template => (
          <div
            key={template.id}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
          >
            <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
            <p className="text-sm text-gray-500 mb-2">{template.description}</p>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>{template.agenda?.length || 0} agenda items</span>
              <span>•</span>
              <span>{template.objectives?.length || 0} objectives</span>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeView) {
      case 'meetings':
        return renderMeetingsList();
      case 'contacts':
        return renderContactsList();
      case 'journal':
        return renderJournalList();
      case 'templates':
        return renderTemplatesList();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 flex items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          <span className="text-lg">Loading diary data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Diary Manager</h2>
            <p className="text-gray-500">Manage meetings, contacts, and journal entries</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAIPrompt(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:opacity-90"
            >
              <Sparkles className="h-4 w-4" />
              AI Context
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Alerts */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span className="text-red-700">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-green-700">{success}</span>
              <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Stats */}
          {renderStats()}

          {/* View Selector */}
          {renderViewSelector()}

          {/* Search & Actions */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${activeView}...`}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Plus className="h-5 w-5" />
              Add {activeView.slice(0, -1)}
            </button>
          </div>

          {/* Content */}
          {renderContent()}
        </div>
      </div>

      {/* AI Context Modal */}
      {showAIPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              AI Context Settings
            </h3>
            <p className="text-gray-600 mb-4">
              Provide context about yourself and your work to help the AI give better suggestions for agendas, notes, and summaries.
            </p>
            <textarea
              value={aiContext}
              onChange={(e) => setAIContext(e.target.value)}
              placeholder="Example: I'm a product manager at a tech startup. I usually have meetings with engineering, design, and marketing teams. My focus is on user experience and growth metrics..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAIPrompt(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAIContext}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Save Context
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddItemModal
          type={activeView}
          onClose={() => setShowAddModal(false)}
          onSave={(data) => {
            switch (activeView) {
              case 'meetings':
                handleCreateMeeting(data);
                break;
              case 'contacts':
                handleCreateContact(data);
                break;
              case 'journal':
                handleCreateJournalEntry(data);
                break;
            }
          }}
          templates={templates}
          enabledDomains={book?.diarySettings?.enabledDomains}
          enabledCategories={book?.diarySettings?.enabledContactCategories}
        />
      )}
    </div>
  );
};

// Add Item Modal Component
const AddItemModal = ({ type, onClose, onSave, templates, enabledDomains, enabledCategories }) => {
  const [formData, setFormData] = useState({
    // Meeting fields
    title: '',
    date: new Date().toISOString().split('T')[0],
    time: '09:00',
    durationMinutes: 60,
    location: '',
    type: 'VIRTUAL',
    // Contact fields
    name: '',
    email: '',
    phone: '',
    category: 'WORK',
    description: '',
    // Journal fields
    domain: 'PERSONAL',
    content: '',
    tags: []
  });

  const [tagInput, setTagInput] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  const renderMeetingForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          placeholder="Meeting title"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => handleChange('date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
          <input
            type="time"
            value={formData.time}
            onChange={(e) => handleChange('time', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
          <select
            value={formData.durationMinutes}
            onChange={(e) => handleChange('durationMinutes', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={45}>45 min</option>
            <option value={60}>1 hour</option>
            <option value={90}>1.5 hours</option>
            <option value={120}>2 hours</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="VIRTUAL">Virtual</option>
            <option value="IN_PERSON">In Person</option>
            <option value="PHONE">Phone Call</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Location / Link</label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) => handleChange('location', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          placeholder="Room name or meeting link"
        />
      </div>
    </div>
  );

  const renderContactForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
        <select
          value={formData.category}
          onChange={(e) => handleChange('category', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        >
          {(enabledCategories || ['WORK', 'FRIEND', 'FAMILY', 'SERVICE']).map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        />
      </div>
    </div>
  );

  const renderJournalForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
        <select
          value={formData.domain}
          onChange={(e) => handleChange('domain', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        >
          {(enabledDomains || ['PERSONAL', 'WORK', 'HEALTH']).map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
        <textarea
          value={formData.content}
          onChange={(e) => handleChange('content', e.target.value)}
          rows={5}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          placeholder="Write your journal entry..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
        <div className="flex gap-2 mb-2 flex-wrap">
          {formData.tags.map(tag => (
            <span
              key={tag}
              className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm flex items-center gap-1"
            >
              #{tag}
              <button onClick={() => removeTag(tag)} className="hover:text-indigo-900">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Add tag"
          />
          <button
            type="button"
            onClick={addTag}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );

  const getTitle = () => {
    switch (type) {
      case 'meetings': return 'Create Meeting';
      case 'contacts': return 'Add Contact';
      case 'journal': return 'New Journal Entry';
      default: return 'Add Item';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold text-gray-900">{getTitle()}</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {type === 'meetings' && renderMeetingForm()}
          {type === 'contacts' && renderContactForm()}
          {type === 'journal' && renderJournalForm()}
        </div>
        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiaryManager;